import { memo, useCallback } from "react";
import { generatePath } from "react-router";

import type { PlanStockObject } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { StockTableRowDailyItem } from "@/components/features/items/stock/_shared/components/table/row/daily-item";
import { StockTableRow } from "@/components/features/items/stock/_shared/components/table/row/row";
import type { PlanStockDiffStore } from "@/components/features/items/stock/plan/model/usecase/use-create-form-model/plan-stock-diff-store";

import { Label } from "@/components/ui/label/label";
import { TableBody } from "@/components/ui/table/table";

type Props = {
	items: PlanStockObject[];
	hotelID: string;

	planStockDiffStore: PlanStockDiffStore;
};

export const PlanStockTableRows = memo(({ items, hotelID, planStockDiffStore }: Props) => {
	const handleSetStock = useCallback(
		(planID: string, date: string) => (isAvailable: boolean, remainNum: number) => {
			planStockDiffStore.put({ planID, date, isAvailable, remainNum });
		},
		[planStockDiffStore],
	);

	return (
		<TableBody>
			{items.map((item) => {
				const { planID, name, isDayTrip, isInSale, isSecret } = item;

				const itemEditPagePath = isDayTrip
					? generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.PLAN_ID.DAYTRIP.DEFAULT, {
							hotelID,
							planID,
						})
					: generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.PLAN_ID.DEFAULT, { hotelID, planID });

				return (
					<StockTableRow
						key={planID}
						itemName={name}
						itemEditPagePath={itemEditPagePath}
						itemLabelChildren={
							<>
								{isDayTrip && <Label variant={"green"} text={"日帰り"} size={"xs"} />}
								{!isInSale && <Label variant={"black"} text={"非公開"} size={"xs"} />}
								{isSecret && <Label variant={"pink"} text={"シークレット"} size={"xs"} />}
							</>
						}
						stockDailyItemChildren={item.stocks.map((stock, idx) => (
							<StockTableRowDailyItem
								key={`${planID}-${stock.date}-${idx}`}
								stockDailyItem={stock}
								onSetStock={handleSetStock(planID, stock.date)}
								currentStockDailyItem={planStockDiffStore.getCurrentPlanStock({
									planID,
									date: stock.date,
									isAvailable: stock.isAvailable,
									remainNum: stock.remainNum,
								})}
							/>
						))}
					/>
				);
			})}
		</TableBody>
	);
});

PlanStockTableRows.displayName = "PlanStockTableRows";
